import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

import MyButton from "../../../components/MyButton";
import MyPagination from "../../../components/MyPagination";
import { useBoardItem } from "../../../hooks/useBoardItem";
import MyLoading from "../../../components/MyLoading";
import { useFile } from "../../../hooks/useFile";
import { AuthContext } from "../../../context/AuthProvider";
import DeleteBoardItemDialog from "../../Common/DeleteBoardItemDialog";
import { PopupContext } from "../../../context/PopupProvider";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";

const News02 = ({ boardCode }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { useGetBoardItems, useGetBoard } = useBoardItem();
  const { downloadFile } = useFile();
  const { roleCode, isAdmin, isAuthenticated, profile } =
    useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItems({
    size: board?.pageSize,
    page,
    sort: "createdDate,Desc",
    boardCode: board?.boardCode,
    enabled: checkRoleAndPermissions(
      roleCode,
      board,
      BOARD_PERMISSION.ACCESS,
      nonPermissions,
      isAdmin
    ),
  });

  const paginationProps = useMemo(
    () => ({
      size: board?.pageSize,
      page,
      setPage,
      maxPage,
      total,
    }),
    [board?.pageSize, maxPage, page, total]
  );

  const mapData = (listData) => {
    setMaxPage(listData.totalPages);
    setTotal(listData.totalElements);
    setContent(listData.content);
  };

  const downloadPdfFile = async (item, key) => {
    let file = null;
    if (key === "kr") {
      file = item.attachedFiles.find(
        (c) =>
          c.contentType === "application/octet-stream" &&
          c.fileName.includes(".pdf") &&
          c.fileName.startsWith("[KR]")
      );
    } else {
      file = item.attachedFiles.find(
        (c) =>
          c.contentType === "application/octet-stream" &&
          c.fileName.includes(".pdf") &&
          c.fileName.startsWith("[EN]")
      );
    }
    if (file) await downloadFile(file);
  };

  const openDeleteDialog = (itemNo) =>
    checkRoleAndPermissions(
      roleCode,
      board,
      BOARD_PERMISSION.WRITE,
      nonPermissions,
      isAdmin
    )
      ? openPopup(
          <DeleteBoardItemDialog boardCode={board?.boardCode} itemNo={itemNo} />
        )
      : alert(getPermissionMessage(BOARD_PERMISSION.WRITE));

  useEffect(() => {
    if (!isLoading && data?.data) {
      mapData(data.data);
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.ACCESS,
        nonPermissions,
        isAdmin
      )
    ) {
      alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.ACCESS}
      boardCode={board?.boardCode}
    >
      <div className="border-t-2 border-primary md:pt-10 pt-5">
        <div className="flex items-center flex-wrap md:gap-4 gap-2 md:mb-10 mb-6">
          <div className="mr-auto">
            <p className="text-gray-dark xl:text-lg lg:text-base text-sm mb-1">
              연차 보고서는 개인별 다운로드 가능합니다. 다만, 무단 복제 및
              배포는 금지되어 있습니다.
            </p>

            <p className="text-gray-dark xl:text-lg lg:text-base text-sm">
              인용이나 웹 게재시 반드시 K-CDH에 사전 문의해 주시기 바랍니다.
            </p>
          </div>

          {checkRoleAndPermissions(
            roleCode,
            board,
            BOARD_PERMISSION.WRITE,
            nonPermissions,
            isAdmin
          ) && (
            <div className="flex justify-end">
              <MyButton
                color="primary"
                onClick={() => navigate("/News/AnnualReport/Add")}
              >
                글 작성하기
              </MyButton>
            </div>
          )}
        </div>

        {isLoading ? (
          <MyLoading className="h-[150px]" />
        ) : (
          <>
            {content?.length ? (
              <div className="flex flex-wrap gap-[4%] lg:gap-y-5 gap-y-4 my-5">
                {content.map((item) => (
                  <div
                    key={item.itemNo}
                    className="border rounded border-gray-border sm:flex-1 lg:min-w-[45%] sm:min-w-[375px] w-full lg:max-w-[48%] flex flex-col"
                  >
                    <div className="xl:py-7 xl:px-8 p-3 flex gap-[4%]">
                      <div className="flex items-center justify-center overflow-hidden lg:w-[148px] w-[120px] lg:min-w-[148px] min-w-[120px] lg:h-[188px] h-[152px]">
                        <img
                          src={
                            item?.thumbnailFiles?.length
                              ? `/api/file/${item.thumbnailFiles[0]?.fileAccessToken}`
                              : "/assets/img/no-image.jpeg"
                          }
                          alt=""
                          className="object-cover w-full h-full"
                        />
                      </div>

                      <div className="annual-content">
                        <h6 className="text-xs text-primary font-medium mb-[2px]">
                          K-CDH
                        </h6>
                        <h2
                          className="font-semibold xl:text-xl overflow-hidden text-ellipsis line-clamp-1"
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            wordBreak: "break-all",
                          }}
                          title={item.title}
                        >
                          {item.title}
                        </h2>
                        <p
                          className="mt-1 text-gray-dark lg:text-base text-sm overflow-hidden text-ellipsis line-clamp-1"
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            wordBreak: "break-all",
                          }}
                        >
                          <b className="font-medium mr-1 lg:text-base text-sm">
                            발행인 :
                          </b>
                          {item.additionalItems?.publisher || "-"}
                        </p>
                        <p
                          className="mt-1 text-gray-dark lg:text-base text-sm overflow-hidden text-ellipsis line-clamp-1"
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            wordBreak: "break-all",
                          }}
                        >
                          <b className="font-medium mr-1 lg:text-base text-sm">
                            발행일 :
                          </b>
                          {item.additionalItems?.publishedDate
                            ? dayjs(item.additionalItems.publishedDate).format(
                                "YYYY-MM-DD"
                              )
                            : "-"}
                        </p>
                        {checkRoleAndPermissions(
                          roleCode,
                          board,
                          BOARD_PERMISSION.READ,
                          nonPermissions,
                          isAdmin
                        ) && (
                          <div className="flex flex-wrap gap-[1%] gap-y-2 lg:mt-4 mt-2">
                            {!!item.attachedFiles.find(
                              (file) =>
                                file.contentType ===
                                  "application/octet-stream" &&
                                file.fileName.includes(".pdf") &&
                                file.fileName.startsWith("[KR]")
                            ) && (
                              <MyButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                className="flex-1"
                                onClick={() => downloadPdfFile(item, "kr")}
                              >
                                <span className="text-[#5757FF] font-semibold lg:text-sm text-xs min-w-max w-full">
                                  PDF 다운로드
                                </span>
                              </MyButton>
                            )}
                            {!!item.attachedFiles.find(
                              (file) =>
                                file.contentType ===
                                  "application/octet-stream" &&
                                file.fileName.includes(".pdf") &&
                                file.fileName.startsWith("[EN]")
                            ) ? (
                              <MyButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                className="flex-1"
                                onClick={() => downloadPdfFile(item, "en")}
                              >
                                <span className="text-[#5757FF] font-semibold lg:text-sm text-xs min-w-max w-full">
                                  PDF 다운로드
                                </span>
                              </MyButton>
                            ) : (
                              <div className="flex-1 min-w-[49%]" />
                            )}
                            {!item.attachedFiles.find(
                              (file) =>
                                file.contentType ===
                                  "application/octet-stream" &&
                                file.fileName.includes(".pdf") &&
                                file.fileName.startsWith("[KR]")
                            ) && <div className="flex-1 min-w-[49%]" />}
                          </div>
                        )}
                      </div>
                    </div>
                    {checkRoleAndPermissions(
                      roleCode,
                      board,
                      BOARD_PERMISSION.WRITE,
                      nonPermissions,
                      isAdmin
                    ) && (
                      <>
                        {isAuthenticated &&
                          (profile?.userId === data?.writer?.userId ||
                            isAdmin) && (
                            <div className="flex justify-end gap-2 xl:px-8 xl:py-4 p-3 border-t border-gray-border mt-auto">
                              <MyButton
                                variant="outlined"
                                size="small"
                                className="w-10"
                                style={{ height: 40 }}
                                onClick={() => openDeleteDialog(item.itemNo)}
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  size="xs"
                                  color="#505050"
                                />
                              </MyButton>
                              <MyButton
                                variant="contained"
                                color="primary"
                                size="small"
                                className="w-10"
                                style={{ height: 40 }}
                                onClick={() =>
                                  checkRoleAndPermissions(
                                    roleCode,
                                    board,
                                    BOARD_PERMISSION.WRITE,
                                    nonPermissions,
                                    isAdmin
                                  )
                                    ? navigate(
                                        `/News/AnnualReport/Edit/${item.itemNo}`
                                      )
                                    : alert(
                                        getPermissionMessage(
                                          BOARD_PERMISSION.WRITE
                                        )
                                      )
                                }
                              >
                                <FontAwesomeIcon icon={faPen} size="xs" />
                              </MyButton>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-dark text-center py-20">
                {t("common.text.noData")}
              </p>
            )}
          </>
        )}

        {maxPage > 1 && (
          <div className="flex justify-center">
            <MyPagination paginationProps={paginationProps} />
          </div>
        )}
      </div>
    </CheckRoleAndPermissionDisplay>
  );
};

export default News02;
